import { Directive, inject, input } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

/** A click on one of these belongs to it: the switch flips, the button acts, and the row stays where it is. */
const CONTROLS = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    '[role="button"]',
    '[role="switch"]',
    '[role="checkbox"]',
    '[contenteditable="true"]',
    '[data-row-link-ignore]'
].join(', ');

/** The middle mouse button. */
const MIDDLE = 1;

/**
 * A table row that opens a page. The whole row takes the click, and Enter opens the focused row. A ctrl-,
 * cmd- or shift-click or a middle click opens it in a new tab. PrimeNG draws the row's focus ring; give the
 * table `[rowHover]="true"` so the row answers the pointer as well.
 *
 * It is still a row, not a link: it has no link role or accessible name, and the browser's context menu
 * has no "open in new tab" for it.
 *
 * A click on a control inside the row is left to the control, and a click that ends a text selection is
 * someone copying a phone number, not opening the row. A disabled PrimeNG control lets clicks through
 * (`.p-disabled` sets `pointer-events: none`), so they land on whatever is under it. Wrap such a control in
 * an element marked `data-row-link-ignore` so that a click on a switch that is still saving doesn't open
 * the row.
 */
@Directive({
    selector: 'tr[appRowLink]',
    host: {
        tabindex: '0',
        '[style.cursor]': '"pointer"',
        '(click)': 'onClick($event)',
        '(auxclick)': 'onAuxClick($event)',
        '(mousedown)': 'onMouseDown($event)',
        '(keydown.enter)': 'onEnter($event)'
    }
})
export class RowLinkDirective {
    private readonly router = inject(Router);

    /** Where the row leads, as router commands: `['/orders', order.id]`. */
    readonly appRowLink = input.required<readonly unknown[]>();

    protected onClick(event: MouseEvent): void {
        if (belongsElsewhere(event)) {
            return;
        }

        if (event.ctrlKey || event.metaKey || event.shiftKey) {
            this.openInNewTab();
            return;
        }

        void this.router.navigateByUrl(this.target());
    }

    protected onAuxClick(event: MouseEvent): void {
        if (event.button === MIDDLE && !belongsElsewhere(event)) {
            this.openInNewTab();
        }
    }

    /** Otherwise the middle button starts the browser's autoscroll, and the click may never come. */
    protected onMouseDown(event: MouseEvent): void {
        if (event.button === MIDDLE && !belongsElsewhere(event)) {
            event.preventDefault();
        }
    }

    /** Only the row itself: Enter on a switch or a button inside it is theirs. */
    protected onEnter(event: Event): void {
        if (event.target === event.currentTarget) {
            void this.router.navigateByUrl(this.target());
        }
    }

    private openInNewTab(): void {
        window.open(this.router.serializeUrl(this.target()), '_blank', 'noopener');
    }

    private target(): UrlTree {
        return this.router.createUrlTree([...this.appRowLink()]);
    }
}

/** The click landed on a control or an area marked to be ignored, or it ends a text selection. */
function belongsElsewhere(event: MouseEvent): boolean {
    const row = event.currentTarget as HTMLElement;
    const control = (event.target as Element).closest(CONTROLS);

    return (!!control && row.contains(control)) || selectsTextIn(row);
}

function selectsTextIn(row: HTMLElement): boolean {
    const selection = window.getSelection();

    return !!selection && !selection.isCollapsed && row.contains(selection.anchorNode);
}
